import React, { useState, useEffect } from 'react';

const ALL_TABS = ['About', 'Work', 'Beyond Code', 'Contact', 'Tutoring'];
const tabToSlug = t => t.toLowerCase().replace(/ /g, '-');
const slugToTab = slug => ALL_TABS.find(t => tabToSlug(t) === slug)?.toLowerCase() ?? 'about';

const Portfolio = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const slug = window.location.pathname.slice(1).toLowerCase();
    return slugToTab(slug);
  });
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [selectedPainting, setSelectedPainting] = useState(null);
  const [tutorForm, setTutorForm] = useState({
    parentName: '', studentName: '', grade: '', subject: '', email: '', message: ''
  });
  const [tutorSubmitted, setTutorSubmitted] = useState(false);

  const tabs = ALL_TABS;

  useEffect(() => {
    const slug = tabToSlug(activeTab);
    const path = slug === 'about' ? '/' : `/${slug}`;
    window.history.pushState(null, '', path);
  }, [activeTab]);

  useEffect(() => {
    const handlePop = () => setActiveTab(slugToTab(window.location.pathname.slice(1).toLowerCase()));
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  const handleTutorSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Tutoring Inquiry – ${tutorForm.studentName}`);
    const body = encodeURIComponent(
      `Parent Name: ${tutorForm.parentName}\nStudent Name: ${tutorForm.studentName}\n` +
      `Grade Level: ${tutorForm.grade}\nSubject Interest: ${tutorForm.subject}\n` +
      `Contact Email: ${tutorForm.email}\n\nMessage:\n${tutorForm.message}`
    );
    window.open(`mailto:soham.das.outreach@gmail.com?subject=${subject}&body=${body}`);
    setTutorSubmitted(true);
  };

  const handleResumeDownload = () => {
    const link = document.createElement('a');
    link.href = '/soham_resume.pdf';
    link.download = 'Soham_Das_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowResumeModal(false);
  };

  const renderAbout = () => (
    <div className="space-y-16 animate-fadeIn">
      {/* Hero */}
      <header className="flex flex-col md:flex-row md:items-center gap-8 md:gap-12">
        {/* Photo */}
        <div 
          className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden flex-shrink-0"
          style={{ 
            border: '2px solid var(--bg-secondary)',
          }}
        >
          <img 
            src="/images/me.jpg" 
            alt="Soham Das"
            className="w-full h-full object-cover"
            style={{ objectPosition: '70% 35%', transform: 'scale(1.6)' }}
          />
        </div>
        
        {/* Name & Title */}
        <div className="space-y-3">
          <h1 
            className="text-5xl md:text-7xl font-medium tracking-tight"
            style={{ fontFamily: 'var(--font-serif)' }}
          >
            Soham Das
          </h1>
          <p className="text-xl md:text-2xl" style={{ color: 'var(--text-secondary)' }}>
            Software Engineer
          </p>
        </div>
      </header>

      {/* Bio */}
      <section className="max-w-2xl space-y-6">
        <p className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          Currently building autonomous robotics systems at the Dynamic Robotics 
          and Artificial Intelligence Laboratory at Oregon State University, working 
          on the DARPA TIAMAT quadruped robotics challenge, where we won 1st place.
        </p>
        <p className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          My work spans perception pipelines, GPU-accelerated multi-container systems, 
          and simulation environments using ROS 2, Isaac Sim, and Habitat.
        </p>
      </section>

      {/* Current */}
      <section className="space-y-8">
        <h2 
          className="text-2xl font-medium"
          style={{ fontFamily: 'var(--font-serif)', color: 'var(--accent)' }}
        >
          Now
        </h2>
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
            <span className="text-lg">Software Engineer</span>
            <span style={{ color: 'var(--text-muted)' }}>Dynamic Robotics and AI Lab · 2025–Present</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
            <span className="text-lg">M.S. Computer Science</span>
            <span style={{ color: 'var(--text-muted)' }}>Oregon State University · 2026–2027</span>
          </div>
        </div>
      </section>

      {/* Education */}
      <section className="space-y-8">
        <h2 
          className="text-2xl font-medium"
          style={{ fontFamily: 'var(--font-serif)', color: 'var(--accent)' }}
        >
          Education
        </h2>
        <div className="space-y-6">
          <div>
            <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
              <span className="text-lg">M.S. in Computer Science</span>
              <span style={{ color: 'var(--text-muted)' }}>Jan 2026 – June 2027</span>
            </div>
            <p style={{ color: 'var(--text-secondary)' }}>Oregon State University · Advisor: Dr. Alan Fern</p>
          </div>
          <div>
            <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
              <span className="text-lg">B.S. in Computer Science</span>
              <span style={{ color: 'var(--text-muted)' }}>Sep 2022 – Dec 2025</span>
            </div>
            <p style={{ color: 'var(--text-secondary)' }}>Oregon State University</p>
          </div>
        </div>
      </section>
    </div>
  );

  const renderWork = () => (
    <div className="space-y-16 animate-fadeIn">
      <header>
        <h1 
          className="text-4xl md:text-5xl font-medium tracking-tight"
          style={{ fontFamily: 'var(--font-serif)' }}
        >
          Selected Work
        </h1>
      </header>

      {/* Experience */}
      <section className="space-y-8">
        <h2 
          className="text-2xl font-medium"
          style={{ fontFamily: 'var(--font-serif)', color: 'var(--accent)' }}
        >
          Experience
        </h2>
        
        <div className="space-y-10">
          <article className="space-y-4 relative">
            {/* Photo with Spot - floating right */}
            <div 
              className="hidden md:block float-right ml-6 mb-4 w-48 h-48 rounded-full overflow-hidden flex-shrink-0"
              style={{ border: '2px solid var(--bg-secondary)', marginTop: '-1rem' }}
            >
              <img 
                src="/images/IMG_1337.jpg" 
                alt="Soham with Boston Dynamics Spot"
                className="w-full h-full object-cover"
                style={{ objectPosition: '50% 30%', transform: 'scale(1.05)' }}
              />
            </div>
            <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
              <h3 className="text-xl">Software Engineer</h3>
              <span style={{ color: 'var(--text-muted)' }}>June 2025 – Present</span>
            </div>
            <p style={{ color: 'var(--text-secondary)' }}>Dynamic Robotics and Artificial Intelligence Laboratory</p>
            <ul className="space-y-2 pl-4" style={{ color: 'var(--text-secondary)' }}>
              <li className="relative before:content-['–'] before:absolute before:-left-4 before:text-[var(--text-muted)]">
                Developed core autonomy, perception, and evaluation infrastructure for the DARPA TIAMAT quadruped robotics challenge — winning 1st place
              </li>
              <li className="relative before:content-['–'] before:absolute before:-left-4 before:text-[var(--text-muted)]">
                Architecting ROS 2–based, GPU-accelerated multi-container systems for SLAM, frontier exploration, and mission execution
              </li>
              <li className="relative before:content-['–'] before:absolute before:-left-4 before:text-[var(--text-muted)]">
                Building reproducible simulation pipelines using Isaac Sim and Habitat
              </li>
            </ul>
          </article>

          <article className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
              <h3 className="text-xl">Undergraduate Learning Assistant</h3>
              <span style={{ color: 'var(--text-muted)' }}>Jan 2025 – June 2025</span>
            </div>
            <p style={{ color: 'var(--text-secondary)' }}>Oregon State University</p>
            <ul className="space-y-2 pl-4" style={{ color: 'var(--text-secondary)' }}>
              <li className="relative before:content-['–'] before:absolute before:-left-4 before:text-[var(--text-muted)]">
                Led weekly studio sessions teaching Python programming fundamentals to freshman CS students
              </li>
              <li className="relative before:content-['–'] before:absolute before:-left-4 before:text-[var(--text-muted)]">
                Mentored students through hands-on coding exercises, debugging sessions, and project development
              </li>
            </ul>
          </article>
        </div>
      </section>

      {/* Projects */}
      <section className="space-y-8">
        <h2 
          className="text-2xl font-medium"
          style={{ fontFamily: 'var(--font-serif)', color: 'var(--accent)' }}
        >
          Projects
        </h2>
        
        <div className="space-y-10">
          <article className="space-y-3">
            <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
              <h3 className="text-xl">2D Physics Engine for RL</h3>
              <span style={{ color: 'var(--text-muted)' }}>Jan 2026</span>
            </div>
            <p style={{ color: 'var(--text-secondary)' }}>
            A deterministic 2D physics engine written in C using semi-implicit Euler integration and 
            the Separating Axis Theorem (SAT) for collision resolution. Bridged the engine core to Python 
            through a C++ wrapper and pybind11 to create a Gymnasium-compliant interface for reinforcement 
            learning tasks. Integrated a JSON-based scene loading system for environment configuration and 
            modeled first-order actuator dynamics to simulate realistic torque responses. Validated the 
            framework by training a PPO agent on a continuous control beam-balancing task.
            </p>
            <div className="mt-3 mb-2 flex justify-center gap-6">
              <div className="flex flex-col items-center">
                <video 
                  src="/544093327-be5a38c5-a90e-4438-8b09-40d59ea6d082.mp4" 
                  controls
                  className="rounded"
                  style={{ maxHeight: '300px', width: 'auto' }}
                />
                <p className="text-sm mt-2 text-center" style={{ color: 'var(--text-secondary)' }}>
                  Trained Policy Controlling Beam
                </p>
              </div>
              <div className="flex flex-col items-center">
                <video 
                  src="/542797765-de39ddf9-bbd8-478f-8692-32b7ad2940a2.mp4" 
                  controls
                  className="rounded"
                  style={{ maxHeight: '300px', width: 'auto' }}
                />
                <p className="text-sm mt-2 text-center" style={{ color: 'var(--text-secondary)' }}>
                  Fun Showcase of Physics
                </p>
              </div>
            </div>
            <a 
              href="https://github.com/Sodas0/rl-phys-eng" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block text-sm transition-colors hover:opacity-70"
              style={{ color: 'var(--accent)' }}
            >
              View on GitHub →
            </a>
          </article>

          <article className="space-y-3">
            <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
              <h3 className="text-xl">SlugNet: Deep Learning Based Computer Vision for Fine-Grained Species Classification</h3>
              <span style={{ color: 'var(--text-muted)' }}>April 2025</span>
            </div>
            <p style={{ color: 'var(--text-secondary)' }}>
              Collected and curated a dataset of over 200,000 slug images. Built a hierarchical 
              classification pipeline: a CNN binary classifier to distinguish slug vs non-slug 
              images with 99% validation accuracy, followed by fine-tuned Vision Transformer 
              models (ViT-B/16, ViT-B/32, ViT-L/16) achieving 81% validation accuracy across 
              75+ species categories.
            </p>
            <div className="mt-3 mb-2 flex justify-center">
              <img 
                src="/images/projects/slugnet-diagram.png" 
                alt="SlugNet architecture diagram showing CNN binary classifier followed by Vision Transformer"
                className="rounded"
                style={{ maxHeight: '300px', width: 'auto' }}
              />
            </div>
            <a 
              href="https://github.com/Sodas0/slug-classification-ViT" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block text-sm transition-colors hover:opacity-70"
              style={{ color: 'var(--accent)' }}
            >
              View on GitHub →
            </a>
          </article>

          <article className="space-y-3">
            <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
              <h3 className="text-xl">Geospatial Ocean Current Visualizer</h3>
              <span style={{ color: 'var(--text-muted)' }}>May 2025</span>
            </div>
            <p style={{ color: 'var(--text-secondary)' }}>
              Developed a robust visualization framework for mapping complex vector fields, 
              streamlines, and separatrices of surface ocean currents. Engineered a high-performance 
              preprocessing pipeline processing over 8 million geospatial data points.
            </p>
            <div className="mt-3 mb-2 flex justify-center">
              <img 
                src="/images/projects/streamlines.png" 
                alt="Surface ocean currents visualization with streamlines showing current speed"
                className="rounded"
                style={{ maxHeight: '300px', width: 'auto' }}
              />
            </div>
            <a 
              href="https://github.com/Sodas0/Ocean-Current-VectorTopology" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block text-sm transition-colors hover:opacity-70"
              style={{ color: 'var(--accent)' }}
            >
              View on GitHub →
            </a>
          </article>
        </div>
      </section>

      {/* Skills */}
      <section className="space-y-8">
        <h2 
          className="text-2xl font-medium"
          style={{ fontFamily: 'var(--font-serif)', color: 'var(--accent)' }}
        >
          Technologies
        </h2>
        <div className="space-y-4" style={{ color: 'var(--text-secondary)' }}>
          <p>
            <span style={{ color: 'var(--text-primary)' }}>Languages:</span>{' '}
            Python, C/C++, Bash, Rust, SQL, TypeScript
          </p>
          <p>
            <span style={{ color: 'var(--text-primary)' }}>Frameworks:</span>{' '}
            PyTorch, NumPy, Pandas, Open3D, OpenCV, LangChain, ROS 2
          </p>
          <p>
            <span style={{ color: 'var(--text-primary)' }}>Tools:</span>{' '}
            Docker, Git, AWS, CUDA, Isaac Sim, Habitat
          </p>
        </div>
      </section>
    </div>
  );

  // Animals - grouped by type
  const animalPaintings = {
    birds: [
      { src: '/images/paintings/Owl.jpg', title: 'Owl', description: 'A watchful great horned owl' },
      { src: '/images/paintings/Hummingbird.jpg', title: 'Hummingbird', description: 'A hummingbird mid-flight' },
      { src: '/images/paintings/🦅🦅🦅🦅🇺🇸🇺🇸🇺🇸🇺🇸🇺🇸.jpg', title: 'Bald Eagle', description: 'America\'s majestic national bird' },
    ],
    mammals: [
      { src: '/images/paintings/Tiger.jpg', title: 'Bengal Tiger', description: 'Portrait of a slightly upset bengal tiger' },
      { src: '/images/paintings/Capy.jpg', title: 'Omnipotent Capy', description: 'An ascended capybara with the world on its head' },
      { src: '/images/paintings/On_Beaver.jpg', title: 'Beaver', description: 'Chomping on a log' },
      { src: '/images/paintings/Tomato_🍅_.jpg', title: 'Tomato', description: 'A cat with a fitting name' },
    ],
    aquatic: [
      { src: '/images/paintings/Whale_Shark.jpg', title: 'Whale Shark', description: 'A gentle giant gliding through the deep blue' },
    ],
  };

  // Landscapes
  const landscapePaintings = [
    { src: '/images/paintings/Ocean.jpg', title: 'View from the Coast', description: 'Ocean horizon viewed from a coastal cliff' },
    { src: '/images/paintings/Waterfall_(erm).jpg', title: 'Cliffside Waterfalls', description: 'Waterfalls descending into a basin at dusk' },
  ];

  const renderBeyondCode = () => (
    <div className="space-y-16 animate-fadeIn">
      <header className="space-y-6">
        <h1 
          className="text-4xl md:text-5xl font-medium tracking-tight"
          style={{ fontFamily: 'var(--font-serif)' }}
        >
          Beyond Code
        </h1>
        <p className="text-lg max-w-xl" style={{ color: 'var(--text-secondary)' }}>
          When I'm not coding, I like to paint. Here's a collection of my favorite pieces, creatively titled.
        </p>
      </header>

      {/* Animals Section */}
      <section className="space-y-12">
        <h2 
          className="text-2xl font-medium"
          style={{ fontFamily: 'var(--font-serif)', color: 'var(--accent)' }}
        >
          Animals
        </h2>
        
        {/* Birds */}
        <div>
          <h3 className="text-lg mb-6" style={{ color: 'var(--text-muted)' }}>Birds</h3>
          <div className="flex flex-wrap justify-center gap-10">
            {animalPaintings.birds.map((painting, index) => (
              <article key={index} className="flex flex-col items-center" style={{ maxWidth: '360px' }}>
                <button 
                  onClick={() => setSelectedPainting(painting)}
                  className="overflow-hidden rounded-lg cursor-pointer"
                  style={{ border: '1px solid var(--bg-secondary)' }}
                >
                  <img 
                    src={painting.src}
                    alt={painting.title}
                    className="transition-transform duration-300 hover:scale-105"
                    style={{ display: 'block', maxHeight: '420px', width: 'auto' }}
                  />
                </button>
                <div className="mt-3 space-y-1 text-center">
                  <h3 className="text-lg font-medium">{painting.title}</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {painting.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Mammals */}
        <div>
          <h3 className="text-lg mb-6" style={{ color: 'var(--text-muted)' }}>Mammals</h3>
          <div className="flex flex-wrap justify-center gap-10">
            {animalPaintings.mammals.map((painting, index) => (
              <article key={index} className="flex flex-col items-center" style={{ maxWidth: '360px' }}>
                <button 
                  onClick={() => setSelectedPainting(painting)}
                  className="overflow-hidden rounded-lg cursor-pointer"
                  style={{ border: '1px solid var(--bg-secondary)' }}
                >
                  <img 
                    src={painting.src}
                    alt={painting.title}
                    className="transition-transform duration-300 hover:scale-105"
                    style={{ display: 'block', maxHeight: '420px', width: 'auto' }}
                  />
                </button>
                <div className="mt-3 space-y-1 text-center">
                  <h3 className="text-lg font-medium">{painting.title}</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {painting.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Aquatic */}
        <div>
          <h3 className="text-lg mb-6" style={{ color: 'var(--text-muted)' }}>Aquatic</h3>
          <div className="flex flex-wrap justify-center gap-10">
            {animalPaintings.aquatic.map((painting, index) => (
              <article key={index} className="flex flex-col items-center" style={{ maxWidth: '400px' }}>
                <button 
                  onClick={() => setSelectedPainting(painting)}
                  className="overflow-hidden rounded-lg cursor-pointer"
                  style={{ border: '1px solid var(--bg-secondary)' }}
                >
                  <img 
                    src={painting.src}
                    alt={painting.title}
                    className="transition-transform duration-300 hover:scale-105"
                    style={{ display: 'block', maxHeight: '500px', width: 'auto' }}
                  />
                </button>
                <div className="mt-3 space-y-1 text-center">
                  <h3 className="text-lg font-medium">{painting.title}</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {painting.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Landscapes Section */}
      <section className="space-y-8">
        <h2 
          className="text-2xl font-medium"
          style={{ fontFamily: 'var(--font-serif)', color: 'var(--accent)' }}
        >
          Landscapes
        </h2>
        
        <div className="flex flex-wrap justify-center gap-10">
          {landscapePaintings.map((painting, index) => (
            <article key={index} className="flex flex-col items-center" style={{ maxWidth: '520px' }}>
              <button 
                onClick={() => setSelectedPainting(painting)}
                className="overflow-hidden rounded-lg cursor-pointer"
                style={{ border: '1px solid var(--bg-secondary)' }}
              >
                <img 
                  src={painting.src}
                  alt={painting.title}
                  className="transition-transform duration-300 hover:scale-105"
                  style={{ display: 'block', maxHeight: '380px', width: 'auto' }}
                />
              </button>
              <div className="mt-3 space-y-1 text-center">
                <h3 className="text-lg font-medium">{painting.title}</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {painting.description}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Painting Lightbox Modal */}
      {selectedPainting && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
          onClick={() => setSelectedPainting(null)}
        >
          <button
            onClick={() => setSelectedPainting(null)}
            className="absolute top-6 right-6 text-3xl transition-opacity hover:opacity-70"
            style={{ color: 'var(--text-secondary)' }}
          >
            ×
          </button>
          <div 
            className="max-w-full max-h-full flex flex-col items-center animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={selectedPainting.src}
              alt={selectedPainting.title}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
              style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}
            />
            <div className="mt-4 text-center">
              <h3 
                className="text-xl font-medium"
                style={{ fontFamily: 'var(--font-serif)', color: 'var(--text-primary)' }}
              >
                {selectedPainting.title}
              </h3>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                {selectedPainting.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderContact = () => (
    <div className="space-y-16 animate-fadeIn">
      <header className="space-y-6">
        <h1 
          className="text-4xl md:text-5xl font-medium tracking-tight"
          style={{ fontFamily: 'var(--font-serif)' }}
        >
          Get in Touch
        </h1>
        <p className="text-lg max-w-xl" style={{ color: 'var(--text-secondary)' }}>
          Open to software engineering roles focused on systems, infrastructure, and end-to-end product development.
        </p>
      </header>

      <section className="space-y-6">
        <a 
          href="mailto:soham.das.outreach@gmail.com"
          className="block text-xl transition-colors hover:opacity-70"
          style={{ color: 'var(--accent)' }}
        >
          soham.das.outreach@gmail.com
        </a>
        
        <div className="flex flex-wrap gap-8 text-lg">
          <button
            onClick={() => setShowResumeModal(true)}
            className="transition-colors hover:opacity-70 cursor-pointer"
            style={{ color: 'var(--text-secondary)' }}
          >
            Resume
          </button>
          <a 
            href="https://github.com/Sodas0" 
            target="_blank" 
            rel="noopener noreferrer"
            className="transition-colors hover:opacity-70"
            style={{ color: 'var(--text-secondary)' }}
          >
            GitHub
          </a>
          <a 
            href="https://www.linkedin.com/in/sohamdas-/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="transition-colors hover:opacity-70"
            style={{ color: 'var(--text-secondary)' }}
          >
            LinkedIn
          </a>
        </div>

        <p className="pt-8" style={{ color: 'var(--text-muted)' }}>
          Based in Corvallis, Oregon
        </p>
      </section>
    </div>
  );

  const renderTutoring = () => (
    <div className="space-y-24 animate-fadeIn">

      {/* Hero */}
      <header className="space-y-8 py-8 md:py-16">
        <div className="space-y-6">
          <h1
            className="text-5xl md:text-7xl font-medium tracking-tight leading-tight"
            style={{ fontFamily: 'var(--font-serif)' }}
          >
            Accelerated STEM<br />Tutoring
          </h1>
          <p className="text-xl md:text-2xl max-w-xl leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            Structured, high-level instruction in mathematics, programming, and robotics for motivated students.
          </p>
        </div>
        <button
          onClick={() => document.getElementById('tutoring-signup')?.scrollIntoView({ behavior: 'smooth' })}
          className="px-8 py-3 text-base font-medium transition-opacity hover:opacity-80"
          style={{
            backgroundColor: 'var(--accent)',
            color: 'var(--bg-primary)',
            borderRadius: '4px',
            letterSpacing: '0.02em'
          }}
        >
          Sign Up Now
        </button>
      </header>

      {/* About the Instructor */}
      <section className="space-y-8">
        <h2
          className="text-2xl font-medium"
          style={{ fontFamily: 'var(--font-serif)', color: 'var(--accent)' }}
        >
          About the Instructor
        </h2>
        <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
          <div
            className="w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden flex-shrink-0"
            style={{ border: '2px solid var(--bg-secondary)' }}
          >
            <img
              src="/images/tutor_headshot.jpg"
              alt="Soham Das"
              className="w-full h-full object-cover"
              style={{ objectPosition: '50% 50%' }}
            />
          </div>
          <div className="space-y-3">
            <div>
              <h3 className="text-xl font-medium">Soham Das</h3>
              <p className="text-sm" style={{ color: 'var(--accent)' }}>M.S. Candidate · AI Robotics Researcher · OSU</p>
            </div>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Soham Das is an M.S. student and AI robotics researcher with experience in machine learning,
              autonomous systems, and computer science education. He helps students build confidence in math,
              coding, and STEM through structured, accelerated learning.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="space-y-8">
        <h2
          className="text-2xl font-medium"
          style={{ fontFamily: 'var(--font-serif)', color: 'var(--accent)' }}
        >
          Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          <div
            className="tutor-card p-6 rounded-lg space-y-5"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.3)'
            }}
          >
            <div className="space-y-1">
              <h3 className="text-xl font-medium" style={{ fontFamily: 'var(--font-serif)' }}>Mathematics</h3>
              <p className="text-xs font-medium tracking-widest uppercase" style={{ color: 'var(--accent)' }}>
                Accelerated & Structured
              </p>
            </div>
            <div className="space-y-2">
              {['AGS 2', 'AGS 3', 'Precalculus'].map(s => (
                <div key={s} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>–</span> {s}
                </div>
              ))}
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              Focus on conceptual understanding, problem solving, and getting ahead before the school year begins.
            </p>
          </div>

          <div
            className="tutor-card p-6 rounded-lg space-y-5"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.3)'
            }}
          >
            <div className="space-y-1">
              <h3 className="text-xl font-medium" style={{ fontFamily: 'var(--font-serif)' }}>Programming</h3>
              <p className="text-xs font-medium tracking-widest uppercase" style={{ color: 'var(--accent)' }}>
                Foundations in CS
              </p>
            </div>
            <div className="space-y-2">
              {['Python fundamentals', 'Problem solving', 'Introductory algorithms', 'Logic & computational thinking'].map(s => (
                <div key={s} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>–</span> {s}
                </div>
              ))}
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              Designed for students interested in coding, software, and future STEM pathways.
            </p>
          </div>

          <div
            className="tutor-card p-6 rounded-lg space-y-5 relative overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid rgba(255,255,255,0.04)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
              opacity: 0.65
            }}
          >
            <div className="absolute top-4 right-4">
              <span
                className="text-xs font-medium px-2 py-1 rounded-full"
                style={{
                  backgroundColor: 'rgba(245,158,11,0.08)',
                  color: 'var(--accent)',
                  border: '1px solid rgba(245,158,11,0.2)',
                  letterSpacing: '0.05em'
                }}
              >
                Coming Soon
              </span>
            </div>
            <div className="space-y-1">
              <h3 className="text-xl font-medium" style={{ fontFamily: 'var(--font-serif)' }}>Robotics</h3>
              <p className="text-xs font-medium tracking-widest uppercase" style={{ color: 'var(--text-muted)' }}>
                Under Development
              </p>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              Hands-on robotics and autonomous systems curriculum currently under development.
              Grounded in real research from DARPA-level competition.
            </p>
          </div>

        </div>
      </section>

      {/* Pricing */}
      <section className="space-y-8">
        <h2
          className="text-2xl font-medium"
          style={{ fontFamily: 'var(--font-serif)', color: 'var(--accent)' }}
        >
          Pricing
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          <div
            className="tutor-card p-8 rounded-lg space-y-6"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.3)'
            }}
          >
            <div className="space-y-2">
              <h3 className="text-2xl font-medium" style={{ fontFamily: 'var(--font-serif)' }}>Private Tutoring</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-medium" style={{ color: 'var(--accent)' }}>$75</span>
                <span style={{ color: 'var(--text-muted)' }}>/ session</span>
              </div>
            </div>
            <ul className="space-y-3">
              {['Personalized instruction', 'Focused acceleration', 'Individual learning plans'].map(item => (
                <li key={item} className="flex items-center gap-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--accent)' }}>–</span> {item}
                </li>
              ))}
            </ul>
          </div>

          <div
            className="tutor-card p-8 rounded-lg space-y-6"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.3)'
            }}
          >
            <div className="space-y-2">
              <h3 className="text-2xl font-medium" style={{ fontFamily: 'var(--font-serif)' }}>Group Tutoring</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-medium" style={{ color: 'var(--accent)' }}>$45</span>
                <span style={{ color: 'var(--text-muted)' }}>/ session</span>
              </div>
            </div>
            <ul className="space-y-3">
              {['Collaborative learning', 'Small group environment', 'STEM enrichment focus'].map(item => (
                <li key={item} className="flex items-center gap-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--accent)' }}>–</span> {item}
                </li>
              ))}
            </ul>
            <p
              className="text-sm leading-relaxed"
              style={{
                color: 'var(--text-muted)',
                borderTop: '1px solid rgba(255,255,255,0.06)',
                paddingTop: '1.25rem'
              }}
            >
              Group sessions are intentionally kept small to maximize engagement and individualized attention.
            </p>
          </div>

        </div>
      </section>

      {/* Sign Up */}
      <section id="tutoring-signup" className="space-y-8 scroll-mt-24">
        <div className="space-y-2">
          <h2
            className="text-2xl font-medium"
            style={{ fontFamily: 'var(--font-serif)', color: 'var(--accent)' }}
          >
            Sign Up
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Fill out the form below and I'll follow up within 48 hours to discuss scheduling and availability.
          </p>
        </div>

        {tutorSubmitted ? (
          <div
            className="p-8 rounded-lg space-y-3 animate-fadeIn"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid rgba(245,158,11,0.2)'
            }}
          >
            <h3 className="text-xl font-medium" style={{ fontFamily: 'var(--font-serif)' }}>
              Thank you for reaching out.
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              I'll be in touch within 48 hours to discuss availability and next steps.
              You can also reach me directly at{' '}
              <a
                href="mailto:soham.das.outreach@gmail.com"
                className="hover:opacity-70 transition-opacity"
                style={{ color: 'var(--accent)' }}
              >
                soham.das.outreach@gmail.com
              </a>
            </p>
          </div>
        ) : (
          <form onSubmit={handleTutorSubmit} className="space-y-5" style={{ maxWidth: '640px' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="block text-sm" style={{ color: 'var(--text-muted)' }}>Parent Name</label>
                <input
                  type="text"
                  required
                  value={tutorForm.parentName}
                  onChange={e => setTutorForm(f => ({ ...f, parentName: e.target.value }))}
                  className="tutor-input w-full px-4 py-3 rounded text-base"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: 'var(--text-primary)'
                  }}
                  placeholder="Jane Smith"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm" style={{ color: 'var(--text-muted)' }}>Student Name</label>
                <input
                  type="text"
                  required
                  value={tutorForm.studentName}
                  onChange={e => setTutorForm(f => ({ ...f, studentName: e.target.value }))}
                  className="tutor-input w-full px-4 py-3 rounded text-base"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: 'var(--text-primary)'
                  }}
                  placeholder="Alex Smith"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="block text-sm" style={{ color: 'var(--text-muted)' }}>Grade Level</label>
                <select
                  required
                  value={tutorForm.grade}
                  onChange={e => setTutorForm(f => ({ ...f, grade: e.target.value }))}
                  className="tutor-input w-full px-4 py-3 rounded text-base"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: tutorForm.grade ? 'var(--text-primary)' : 'var(--text-muted)'
                  }}
                >
                  <option value="" disabled>Select grade</option>
                  {['6th Grade', '7th Grade', '8th Grade', '9th Grade', '10th Grade', '11th Grade', '12th Grade', 'College'].map(g => (
                    <option key={g} value={g} style={{ backgroundColor: '#111111', color: '#e5e5e5' }}>{g}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm" style={{ color: 'var(--text-muted)' }}>Subject Interest</label>
                <select
                  required
                  value={tutorForm.subject}
                  onChange={e => setTutorForm(f => ({ ...f, subject: e.target.value }))}
                  className="tutor-input w-full px-4 py-3 rounded text-base"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: tutorForm.subject ? 'var(--text-primary)' : 'var(--text-muted)'
                  }}
                >
                  <option value="" disabled>Select subject</option>
                  {['Mathematics', 'Programming', 'Other'].map(s => (
                    <option key={s} value={s} style={{ backgroundColor: '#111111', color: '#e5e5e5' }}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm" style={{ color: 'var(--text-muted)' }}>Email</label>
              <input
                type="email"
                required
                value={tutorForm.email}
                onChange={e => setTutorForm(f => ({ ...f, email: e.target.value }))}
                className="tutor-input w-full px-4 py-3 rounded text-base"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'var(--text-primary)'
                }}
                placeholder="jane@example.com"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm" style={{ color: 'var(--text-muted)' }}>Message</label>
              <textarea
                rows={4}
                value={tutorForm.message}
                onChange={e => setTutorForm(f => ({ ...f, message: e.target.value }))}
                className="tutor-input w-full px-4 py-3 rounded text-base"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'var(--text-primary)',
                  resize: 'vertical'
                }}
                placeholder="Tell me about your student's goals, current level, and any specific topics you'd like to focus on..."
              />
            </div>
            <button
              type="submit"
              className="px-8 py-3 text-base font-medium transition-opacity hover:opacity-80"
              style={{
                backgroundColor: 'var(--accent)',
                color: 'var(--bg-primary)',
                borderRadius: '4px',
                letterSpacing: '0.02em'
              }}
            >
              Send Inquiry
            </button>
          </form>
        )}
      </section>

    </div>
  );

  const renderContent = () => {
    switch (activeTab.toLowerCase()) {
      case 'about': return renderAbout();
      case 'work': return renderWork();
      case 'beyond code': return renderBeyondCode();
      case 'contact': return renderContact();
      case 'tutoring': return renderTutoring();
      default: return renderAbout();
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
    >
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-sm" style={{ backgroundColor: 'rgba(10, 10, 10, 0.9)' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="flex flex-wrap justify-center gap-6 md:gap-10 py-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab.toLowerCase())}
                className="relative text-lg transition-colors"
                style={{ 
                  color: activeTab === tab.toLowerCase() 
                    ? 'var(--text-primary)' 
                    : 'var(--text-muted)',
                  fontFamily: 'var(--font-sans)'
                }}
              >
                {tab}
                {activeTab === tab.toLowerCase() && (
                  <span 
                    className="absolute -bottom-1 left-0 w-full h-px"
                    style={{ backgroundColor: 'var(--accent)' }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main 
        className={`flex-1 mx-auto w-full px-6 md:px-8 py-16 md:py-24 ${
          activeTab === 'beyond code' ? 'max-w-7xl' : 'max-w-4xl'
        }`}
        key={activeTab}
      >
        {renderContent()}
      </main>

      {/* Footer */}
      <footer className="py-8" style={{ borderTop: '1px solid var(--bg-secondary)' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            © 2025 Soham Das
          </p>
        </div>
      </footer>

      {/* Resume Download Modal */}
      {showResumeModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={() => setShowResumeModal(false)}
        >
          <div 
            className="max-w-md w-full p-8 rounded-lg space-y-6 animate-fadeIn"
            style={{ 
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--bg-secondary)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 
              className="text-2xl font-medium"
              style={{ fontFamily: 'var(--font-serif)' }}
            >
              Download Resume
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              Would you like to download my resume as a PDF?
            </p>
            <div className="flex gap-4">
              <button
                onClick={handleResumeDownload}
                className="px-6 py-2 transition-colors hover:opacity-70"
                style={{ 
                  backgroundColor: 'var(--accent)',
                  color: 'var(--bg-primary)'
                }}
              >
                Download
              </button>
              <button
                onClick={() => setShowResumeModal(false)}
                className="px-6 py-2 transition-colors hover:opacity-70"
                style={{ 
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--bg-secondary)'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
        .tutor-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .tutor-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.45) !important;
        }
        .tutor-input {
          outline: none;
          transition: border-color 0.15s ease;
        }
        .tutor-input:focus {
          border-color: rgba(245,158,11,0.4) !important;
        }
      `}</style>
    </div>
  );
};

export default Portfolio;
